import React from 'react';
import { Clock, CheckCircle, Eye, Zap } from 'lucide-react';
interface TimelineItem {
  time: string;
  action: string;
  type: 'success' | 'view' | 'action' | 'automation';
}
interface ActivityTimelineProps {
  activeFeature: number;
}
const timelineData: Record<number, TimelineItem[]> = {
  0: [{
    time: '2 min ago',
    action: 'New offer accepted - Austin, TX',
    type: 'success'
  }, {
    time: '15 min ago',
    action: 'Agent viewed offer - Deal #1247',
    type: 'view'
  }, {
    time: '1 hour ago',
    action: 'Bulk offer sent to 23 agents',
    type: 'action'
  }],
  1: [{
    time: '5 min ago',
    action: 'Template "Cash Offer" converted',
    type: 'success'
  }, {
    time: '20 min ago',
    action: 'Performance report generated',
    type: 'action'
  }, {
    time: '45 min ago',
    action: 'Agent engagement tracked',
    type: 'view'
  }],
  2: [{
    time: '10 min ago',
    action: 'Deal moved to "Closing Soon"',
    type: 'success'
  }, {
    time: '30 min ago',
    action: 'Pipeline health check completed',
    type: 'action'
  }, {
    time: '1 hour ago',
    action: 'At-risk deal flagged for review',
    type: 'view'
  }],
  3: [{
    time: '3 min ago',
    action: 'Market trend alert - Austin up 7%',
    type: 'success'
  }, {
    time: '25 min ago',
    action: 'Zip code 78701 performance updated',
    type: 'action'
  }, {
    time: '50 min ago',
    action: 'DOM analysis completed',
    type: 'view'
  }]
};
const iconMap = {
  success: CheckCircle,
  view: Eye,
  action: Zap,
  automation: Zap
};
const colorMap = {
  success: 'text-[#3ab795] bg-[#3ab795]/10',
  view: 'text-blue-400 bg-blue-400/10',
  action: 'text-purple-400 bg-purple-400/10',
  automation: 'text-yellow-400 bg-yellow-400/10'
};
/**
 * ActivityTimeline component
 * Shows recent activity feed for the active feature
 */
export function ActivityTimeline({
  activeFeature
}: ActivityTimelineProps) {
  const items = timelineData[activeFeature] || timelineData[0];
  return <div className="space-y-3" data-id="element-139">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-140">
        <div className="flex items-center gap-2" data-id="element-141">
          <Clock className="w-4 h-4 text-[#3ab795]" data-id="element-142" />
          <h4 className="text-sm font-semibold text-white" data-id="element-143">Recent Activity</h4>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-144">
          <div className="w-1.5 h-1.5 rounded-full bg-[#3ab795] animate-pulse" data-id="element-145" />
          <span className="text-xs text-[#3ab795] font-medium" data-id="element-146">Live</span>
        </div>
      </div>

      {/* Timeline Items */}
      <div className="space-y-3" data-id="element-147">
        {items.map((item, index) => {
        const Icon = iconMap[item.type];
        const colorClass = colorMap[item.type];
        return <div key={index} className="flex items-start gap-3 group hover:bg-white/5 p-2 rounded-lg transition-colors duration-200" style={{
          animation: `slideInLeft 0.4s ease-out ${index * 0.1}s both`
        }} data-id="element-148">
              {/* Icon */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center`} data-id="element-149">
                <Icon className="w-4 h-4" strokeWidth={2} data-id="element-150" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-151">
                <p className="text-sm text-white/90 leading-snug" data-id="element-152">
                  {item.action}
                </p>
                <p className="text-xs text-[#b8bcc8] mt-0.5" data-id="element-153">{item.time}</p>
              </div>
            </div>;
      })}
      </div>

      {/* View All Link */}
      <button className="w-full mt-4 text-xs text-[#3ab795] hover:text-[#2d9f7e] font-medium flex items-center justify-center gap-1 py-2 hover:bg-[#3ab795]/5 rounded-lg transition-colors" data-id="element-154">
        <span data-id="element-155">View all activity</span>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-156">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-157" />
        </svg>
      </button>
    </div>;
}